/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useRef, useState, useCallback, ChangeEvent } from "react";
import { TextField, InputAdornment } from "@mui/material";

interface Props {
  label?: string;
  value?: string;
  maxLength: number;
  onChanged: (text: string) => void;
}

export const CountTextField: FC<Props> = ({
  label,
  value,
  maxLength,
  onChanged,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const inComposition = useRef(false);
  const notCompositionValue = useRef<string>();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let str = event.target.value;
      if (!inComposition.current) {
        str = str.match(/[a-zA-Z0-9!-/:-@¥[-`{-~\s]/g)?.join("") ?? "";
        if (str.length > maxLength) {
          str = notCompositionValue.current ?? str.substring(0, maxLength);
        }
      }
      setLocalValue((pre) => {
        if (pre !== str) {
          return str;
        }
        return pre;
      });
      if (!inComposition.current) {
        onChanged(str);
      }
    },
    [maxLength, onChanged]
  );

  const handleCompositionStart = useCallback(() => {
    inComposition.current = true;
    notCompositionValue.current = localValue;
  }, [localValue]);

  const handleCompositionEnd = useCallback(
    (e: unknown) => {
      inComposition.current = false;
      handleChange(e as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
      notCompositionValue.current = undefined;
    },
    [handleChange]
  );

  return (
    <TextField
      label={label}
      variant="outlined"
      multiline
      rows={4}
      value={localValue}
      onChange={handleChange}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      inputProps={{
        maxLength: maxLength,
      }}
      fullWidth
      sx={{
        position: "relative",
        backgroundColor: "white",
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{ position: "absolute", right: "1rem", bottom: "1.5rem" }}
            disablePointerEvents={true}
            disableTypography={true}
          >
            {inComposition.current
              ? notCompositionValue.current?.length
              : localValue?.length}
            /{maxLength}
          </InputAdornment>
        ),
      }}
    />
  );
};
